import React, { useState, useEffect, useRef } from 'react';
import { 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Typography, 
  Checkbox, 
  FormControlLabel,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import { Snackbar, Alert } from '@mui/material';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { useLoader } from '../../context/LoaderContext.js';
import { uploadXlsxFile } from '../../api/appWorkplacesUsersApi.js';
import { useDomain } from '../../context/DomainContext.js';

const DataUploader = () => {
  const [data, setData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [openSnackbar, setOpenSnackbar] = useState({ open: false, text: '', error: false });
  const [showData, setShowData] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const { showLoader, hideLoader } = useLoader();
  const { selectedDomain } = useDomain();
  const fileInputRef = useRef(null);

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setOpenSnackbar({ open: false, text: '', error: false });
  };

  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
  };

  const resetUploader = () => {
    setData([]);
    setHeaders([]);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadDataToApi = async (file, selectedDomain) => {
    try {
      showLoader();
      await uploadXlsxFile(file, selectedDomain);
      setOpenSnackbar({ open: true, text: 'Archivo cargado correctamente.', error: false });
      resetUploader();
    } catch (error) {
      console.error('Error al enviar los datos a la API:', error);
      setOpenSnackbar({ open: true, text: 'Hubo un error al cargar los datos a la API.', error: true });
    } finally {
      hideLoader();
    }
  };

  const handleConfirmUpload = () => {
    handleCloseConfirmDialog();
    if (selectedFile) {
      uploadDataToApi(selectedFile, selectedDomain);
    }
  };

  const processExcelFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { 
          type: 'array',
          cellDates: true,
          cellStyles: true,
          cellNF: true
        });
        
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          raw: false,
          dateNF: 'yyyy-mm-dd'
        });

        const headers = jsonData[0];
        const rows = jsonData.slice(1).map(row => {
          const rowData = {};
          headers.forEach((header, index) => {
            rowData[header] = row[index] || '';
          });
          return rowData;
        });

        setHeaders(headers);
        setData(rows);
      } catch (error) {
        console.error('Error al procesar el archivo Excel:', error);
        setOpenSnackbar({ open: true, text: 'Error al procesar el archivo Excel', error: true });
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setSelectedFile(file);
    const fileExtension = file.name.split('.').pop().toLowerCase();
    
    if (fileExtension === 'csv') {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (result) => {
          setHeaders(Object.keys(result.data[0] || {}));
          setData(result.data);
        },
        error: (error) => {
          console.error('Error al procesar el archivo CSV:', error);
          setOpenSnackbar({ open: true, text: 'Error al procesar el archivo CSV', error: true });
        },
      });
    } else if (['xlsx', 'xls'].includes(fileExtension)) {
      processExcelFile(file);
    } else {
      setOpenSnackbar({ 
        open: true, 
        text: 'Formato de archivo no soportado. Por favor, use CSV, XLS o XLSX', 
        error: true 
      });
    }
  };

  useEffect(() => {
    hideLoader();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      style={{
        padding: "16px",
        maxHeight: "calc(-234px + 100vh)",
        overflow: "auto",
        display: "grid",
        gap: "16px"
      }}
    >
      <Button
        variant="contained"
        component="label"
        style={{
          width: "fit-content"
        }}
      >
        Seleccionar archivo
        <input
          type="file"
          accept=".csv,.xlsx,.xls"
          hidden
          data-testid="file-input"
          onChange={handleFileSelect}
          ref={fileInputRef}
        />
      </Button>

      {selectedFile && (
        <Typography variant="body2">
          Archivo seleccionado: {selectedFile.name}
        </Typography>
      )}

      <Typography variant="body2" color="textSecondary" style={{ width: "35%" }}>
        El proceso de carga permite importar información desde archivos CSV, XLS o XLSX,
        incorporando cuentas de dominio con sus respectivos atributos,
        así como palabras reservadas utilizadas en el sistema.
        Es importante asegurarse de que el archivo cumpla con el formato adecuado
        y contenga datos correctos para evitar inconsistencias.
      </Typography>

      <FormControlLabel
        control={
          <Checkbox
            checked={showData}
            onChange={() => setShowData(!showData)}
            inputProps={{ 'aria-label': 'controlled' }}
            style={{
              width: "fit-content"
            }}
          />
        }
        label="Mostrar datos"
      />

      {showData && data.length > 0 && (
        <>
          <TableContainer component={Paper} style={{ maxHeight: "400px", maxWidth: "100%", overflow: "auto" }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {Array.isArray(headers) && headers.map((header, index) => (
                    <TableCell key={index}>{header}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.isArray(data) && data.map((row, rowIndex) => (
                  <TableRow key={rowIndex}>
                    {Array.isArray(headers) && headers.map((header, cellIndex) => (
                      <TableCell key={cellIndex}>{row[header]}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpenConfirmDialog(true)}
            style={{ width: "fit-content" }}
            disabled={!selectedFile}
          >
            Confirmar carga
          </Button>
        </>
      )}

      <Dialog
        open={openConfirmDialog}
        onClose={handleCloseConfirmDialog}
      >
        <DialogTitle>Confirmar carga de archivo</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Está seguro que desea cargar el archivo {selectedFile?.name}? 
            Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} color="primary">
            Cancelar
          </Button>
          <Button onClick={handleConfirmUpload} color="primary" variant="contained">
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        autoHideDuration={6000}
        open={openSnackbar.open}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={openSnackbar.error ? 'error' : 'success'}
          variant="filled"
        >
          {openSnackbar.text}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default DataUploader;